"use client";

import React from "react";
import { ArrowRight, Activity, Disc, Sparkles, Layers, Cpu, ChartLine as LineChart } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const featuredCards = [{
  title: "Blend Uniformity",
  description: "Real-time non-destructive quantification of powder blend homogeneity and active pharmaceutical ingredient distribution.",
  href: "/SolutionsPage",
  tag: "PAT & In-Line NIR",
  icon: Disc,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_monitoring.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_monitoring.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_monitoring.png-wvc-sizes",
  metric: "Sub-second acquisition"
}, {
  title: "Drying Endpoint",
  description: "Continuous moisture profiling and precise endpoint determination for fluid bed, vacuum, and tray drying processes.",
  href: "/SolutionsPage",
  tag: "Process Analytical Tech",
  icon: Activity,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_control.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_control.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_control.png-wvc-sizes",
  metric: "Continuous feedback loop"
}];
const standardApplications = [{
  name: "Moisture Prediction",
  icon: Sparkles
}, {
  name: "Reaction Monitoring",
  icon: Activity
}, {
  name: "Crystallisation Monitoring",
  icon: Layers
}, {
  name: "Continuous Manufacturing",
  icon: Cpu
}, {
  name: "Chemometrics & Multivariate Modeling",
  icon: LineChart
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="featuredapplications" data-nav="dark" className="relative bg-sidebar text-sidebar-foreground py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative glow accents */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-sidebar-accent/30 rounded-full blur-3xl pointer-events-none" />

      <div className="page-container relative z-10">
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sidebar-accent text-primary text-xs font-semibold uppercase tracking-wider mb-4 border border-sidebar-border">
            <Sparkles className="size-3.5" />
            <span>Targeted Applications</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-sidebar-foreground tracking-tight">
            Process Analytical Solutions for Regulated Manufacturing
          </h2>
          <p className="mt-4 text-base md:text-lg text-sidebar-foreground/80 leading-relaxed font-default">
            Proven spectroscopic and analytical workflows engineered to meet stringent QA/QC protocols across Ireland and UK manufacturing facilities.
          </p>
        </div>

        {/* 2 Primary Featured Application Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {featuredCards.map((card, i) => {
          const Icon = card.icon;
          return <motion.div key={card.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-sidebar-accent/70 border-sidebar-border/80 text-sidebar-foreground backdrop-blur-xs transition-[border-color,box-shadow,transform] hover:border-primary/50 hover:shadow-lg flex flex-col justify-between overflow-hidden group">
                  <div className="relative h-48 sm:h-56 w-full overflow-hidden">
                    <img src={card.image} data-wvc-srcset={card.srcset} data-wvc-sizes={card.sizes} alt={card.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-sidebar/60 transition-opacity group-hover:bg-sidebar/40" />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-primary text-primary-foreground border-transparent font-medium shadow-xs">
                        {card.tag}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 right-4 bg-sidebar/90 text-sidebar-foreground text-xs px-2.5 py-1 rounded-md border border-sidebar-border backdrop-blur-xs font-medium">
                      {card.metric}
                    </div>
                  </div>

                  <CardHeader className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-primary/20 text-primary border border-primary/30">
                        <Icon className="size-5" />
                      </div>
                      <CardTitle className="text-2xl font-default font-semibold text-sidebar-foreground">
                        {card.title}
                      </CardTitle>
                    </div>
                    <CardDescription className="text-sidebar-foreground/80 text-base font-default mt-2">
                      {card.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="h-px w-full bg-sidebar-border" />
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                      <Link to={card.href} className="flex items-center justify-center gap-2">
                        Explore {card.title}
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* 5-Item Badge Cluster of Non-Linked Applications */}
        <div className="pt-8 border-t border-sidebar-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="shrink-0">
              <span className="text-xs uppercase tracking-wider font-semibold text-sidebar-foreground/70 block">
                Additional Supported Methods
              </span>
              <p className="text-sm text-sidebar-foreground font-medium mt-1">
                Integrated online and benchtop process monitoring
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
              {standardApplications.map((app, i) => {
              const Icon = app.icon;
              return <div key={app.name} data-index={i} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sidebar-accent/50 text-sidebar-foreground border border-sidebar-border text-sm font-medium transition-colors hover:border-primary/40 hover:bg-sidebar-accent">
                    <Icon className="size-4 text-primary" />
                    <span>{app.name}</span>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}